import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Modal, IconButton, Typography, Chip } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import CampaignIcon from "@mui/icons-material/Campaign";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
// import DiscountIcon from "@mui/icons-material/Discount";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import axios from "axios";
import MDBox from "components/MDBox";

// Design tokens matching the provided mockups
const COLORS = {
  blue: "#2F80ED",
  blueDark: "#1C64D1",
  blueBg: "#EAF3FE",
  blueBorder: "#D6E9FC",
  green: "#219653",
  greenDark: "#17703E",
  greenBg: "#EAF8EF",
  greenBorder: "#D3F0DE",
  greenIconBg: "#D6F2E1",
  text: "#1A1A1A",
  textSecondary: "#6B7280",
  border: "#EDEEF1",
};

function IconBadge({ icon, bg, color, size }) {
  return (
    <MDBox
      sx={{
        width: size,
        height: size,
        minWidth: size,
        borderRadius: "10px",
        backgroundColor: bg,
        color,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {icon}
    </MDBox>
  );
}
IconBadge.propTypes = {
  icon: PropTypes.node.isRequired,
  bg: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  size: PropTypes.number,
};
IconBadge.defaultProps = { size: 40 };

function PanelHeader({
  icon,
  iconBg,
  iconColor,
  title,
  titleColor,
  subtitle,
  bg,
  borderColor,
  active,
  clickable,
  onClick,
  sx,
}) {
  return (
    <MDBox
      onClick={onClick}
      sx={{
        backgroundColor: bg,
        border: `1px solid ${borderColor}`,
        borderRadius: "12px",
        p: 2,
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        cursor: clickable ? "pointer" : "default",
        borderBottom: active ? `3px solid ${titleColor}` : `1px solid ${borderColor}`,
        transition: "background-color .15s ease, border-color .15s ease",
        boxSizing: "border-box",
        ...sx,
      }}
    >
      <IconBadge icon={icon} bg={iconBg} color={iconColor} size={38} />
      <MDBox sx={{ minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: { xs: 14, md: 17 }, color: titleColor, lineHeight: 1.25 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: { xs: 11.5, md: 12.5 }, color: COLORS.textSecondary, mt: 0.3, lineHeight: 1.35 }}>
          {subtitle}
        </Typography>
      </MDBox>
    </MDBox>
  );
}
PanelHeader.propTypes = {
  icon: PropTypes.node.isRequired,
  iconBg: PropTypes.string.isRequired,
  iconColor: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  titleColor: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  bg: PropTypes.string.isRequired,
  borderColor: PropTypes.string.isRequired,
  active: PropTypes.bool,
  clickable: PropTypes.bool,
  onClick: PropTypes.func,
  sx: PropTypes.object,
};
PanelHeader.defaultProps = { active: false, clickable: false, onClick: undefined, sx: {} };

function ViewAllLink({ label, color, onClick }) {
  return (
    <Typography
      component="span"
      onClick={onClick}
      sx={{
        fontSize: 13.5,
        fontWeight: 600,
        color,
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        cursor: onClick ? "pointer" : "default",
      }}
    >
      {label} <ArrowForwardIcon sx={{ fontSize: 15 }} />
    </Typography>
  );
}
ViewAllLink.propTypes = {
  label: PropTypes.string.isRequired,
  color: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};
ViewAllLink.defaultProps = { onClick: undefined };

// Remove: import DiscountIcon from "@mui/icons-material/Discount";

function PercentSealIcon({ sx }) {
  return (
    <svg viewBox="0 0 24 24" width="35" height="35" style={sx} fill="none">
      <path
        d="M12 1.5l2.09 1.52 2.58-.24 1.02 2.4 2.4 1.02-.24 2.58 1.52 2.09-1.52 2.09.24 2.58-2.4 1.02-1.02 2.4-2.58-.24L12 22.5l-2.09-1.52-2.58.24-1.02-2.4-2.4-1.02.24-2.58L2.63 12.13l1.52-2.09-.24-2.58 2.4-1.02 1.02-2.4 2.58.24L12 1.5z"
        fill="currentColor"
      />
      <path
        d="M9 9.5a1.5 1.5 0 113 0 1.5 1.5 0 01-3 0zM15 14.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0z"
        fill="#fff"
      />
      <path d="M8.5 15.5l7-7" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}
PercentSealIcon.propTypes = { sx: PropTypes.object };
PercentSealIcon.defaultProps = { sx: {} };

function AnnouncementRow({ item, showNew }) {
  return (
    <MDBox
      sx={{
        display: "flex",
        gap: 1.5,
        py: 2,
        px: { xs: 2, md: 2.5 },
        borderBottom: `1px solid ${COLORS.border}`,
        "&:last-of-type": { borderBottom: "none" },
      }}
    >
      <MDBox sx={{ width: 6, display: "flex", justifyContent: "center", pt: "8px" }}>
        <MDBox sx={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: COLORS.blue }} />
      </MDBox>
      <IconBadge icon={<CampaignIcon sx={{ fontSize: 19 }} />} bg={COLORS.blueBg} color={COLORS.blue} size={38} />
      <MDBox sx={{ flex: 1, minWidth: 0 }}>
        <MDBox sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 15, color: COLORS.text, lineHeight: 1.35 }}>
            {item.text}
          </Typography>
          {showNew && (
            <Chip
              label="New"
              size="small"
              sx={{
                height: 20,
                fontSize: 11,
                fontWeight: 600,
                backgroundColor: COLORS.blueBg,
                color: COLORS.blueDark,
              }}
            />
          )}
        </MDBox>
      </MDBox>
    </MDBox>
  );
}
AnnouncementRow.propTypes = {
  item: PropTypes.shape({
    text: PropTypes.string,
    offersAndNews: PropTypes.string,
  }).isRequired,
  showNew: PropTypes.bool,
};
AnnouncementRow.defaultProps = { showNew: false };

function OfferCard({ item }) {
  return (
    <MDBox
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        backgroundColor: COLORS.greenBg,
        border: `1px solid ${COLORS.greenBorder}`,
        borderRadius: "10px",
        p: 2,
        mb: 1.5,
        "&:last-of-type": { mb: 0 },
      }}
    >
      {/* <IconBadge icon={<DiscountIcon sx={{ fontSize: 19 }} />} bg={COLORS.greenIconBg} color={COLORS.greenDark} size={38} /> */}
      <IconBadge icon={<PercentSealIcon />} bg={COLORS.greenIconBg} color={COLORS.greenDark} size={38} />
      <MDBox sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 15, color: COLORS.text, mb: 1, lineHeight: 1.35 }}>
          {item.text}
        </Typography>
        <MDBox
          component="button"
          type="button"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 0.5,
            fontFamily: "inherit",
            textTransform: "none",
            fontWeight: 600,
            fontSize: 13,
            color: COLORS.green,
            border: `1px solid ${COLORS.green}`,
            borderRadius: "20px",
            px: 1.5,
            py: 0.5,
            backgroundColor: "#fff",
            cursor: "pointer",
          }}
        >
          View Offer <ArrowForwardIcon sx={{ fontSize: 15 }} />
        </MDBox>
      </MDBox>
    </MDBox>
  );
}
OfferCard.propTypes = {
  item: PropTypes.shape({
    text: PropTypes.string,
    offersAndNews: PropTypes.string,
  }).isRequired,
};

function EmptyState({ label }) {
  return (
    <Typography sx={{ p: 3, color: COLORS.textSecondary, fontSize: 14, textAlign: "center" }}>{label}</Typography>
  );
}
EmptyState.propTypes = { label: PropTypes.string.isRequired };

function Popup({ open, onClose }) {
  const [news, setNews] = useState([]);
  const [offers, setOffers] = useState([]);
  const [activeTab, setActiveTab] = useState("announcements");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const sessionToken = localStorage.getItem("sessionToken");

        if (!sessionToken) {
          console.error("Session token not found. Please log in again.");
          return;
        }

        const response = await axios.get(
          "https://api.exampladmedia.com/api/offerAndNews/getAllOfferAndNews",
          {
            headers: {
              Authorization: sessionToken,
            },
          }
        );

        const data = response.data.data || [];
        setNews(data.filter((item) => item.type === "NEWS"));
        setOffers(data.filter((item) => item.type === "OFFERS"));
      } catch (error) {
        console.error("Error fetching announcements/offers:", error);
      }
    };

    fetchData();
  }, [open]);

  return (
    <Modal open={open} onClose={onClose}>
      <MDBox
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: {
            xs: "94%",
            sm: "85%",
            md: "760px",
            lg: "920px",
          },
          maxHeight: "90vh",
          outline: "none",
        }}
      >
        <MDBox
          sx={{
            backgroundColor: "#fff",
            borderRadius: { xs: "20px 20px 0 0", md: "16px" },
            position: "relative",
            boxShadow: "0 10px 40px rgba(0,0,0,0.18)",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {/* Mobile drag handle */}
          <MDBox sx={{ display: { xs: "flex", md: "none" }, justifyContent: "center", pt: 1.5 }}>
            <MDBox sx={{ width: 40, height: 4, borderRadius: 2, backgroundColor: "#D1D5DB" }} />
          </MDBox>

          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              zIndex: 10,
              width: 32,
              height: 32,
              backgroundColor: "#fff",
              border: "1px solid #E5E7EB",
              boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
              "&:hover": { backgroundColor: "#f5f5f5" },
            }}
          >
            <ClearIcon sx={{ fontSize: 18, color: "#111" }} />
          </IconButton>

          <MDBox sx={{ p: { xs: 2, md: 3 }, pt: { xs: 1, md: 3 }, flex: 1, minHeight: 0, overflowY: "auto" }}>
            {/* ---------------- DESKTOP ---------------- */}
            <MDBox sx={{ display: { xs: "none", md: "flex" }, gap: 2.5 }}>
              {/* Announcements column */}
              <MDBox sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
                <PanelHeader
                  icon={<CampaignIcon sx={{ fontSize: 20 }} />}
                  iconBg="#DCEBFD"
                  iconColor={COLORS.blue}
                  title="Announcements"
                  titleColor={COLORS.text}
                  subtitle="Stay updated with the latest news and updates."
                  bg={COLORS.blueBg}
                  borderColor={COLORS.blueBorder}
                />
                <MDBox sx={{ mt: 2, border: `1px solid ${COLORS.border}`, borderRadius: "12px", overflow: "hidden" }}>
                  <MDBox sx={{ maxHeight: 420, overflowY: "auto" }}>
                    {news.length === 0 ? (
                      <EmptyState label="No announcements available." />
                    ) : (
                      news.map((item, idx) => (
                        <AnnouncementRow key={`${item.offersAndNews}-${idx}`} item={item} showNew={idx === 0} />
                      ))
                    )}
                  </MDBox>
                  {news.length > 0 && (
                    <MDBox sx={{ textAlign: "center", py: 1.5, borderTop: `1px solid ${COLORS.border}` }}>
                      <ViewAllLink label="View all announcements" color={COLORS.blue} />
                    </MDBox>
                  )}
                </MDBox>
              </MDBox>

              {/* Offers column */}
              <MDBox sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
                <PanelHeader
                  icon={<LocalOfferOutlinedIcon sx={{ fontSize: 20 }} />}
                  iconBg={COLORS.greenIconBg}
                  iconColor={COLORS.greenDark}
                  title="Offers"
                  titleColor={COLORS.text}
                  subtitle="Take advantage of exclusive offers and discounts."
                  bg={COLORS.greenBg}
                  borderColor={COLORS.greenBorder}
                />
                <MDBox sx={{ mt: 2, flex: 1, display: "flex", flexDirection: "column" }}>
                  <MDBox sx={{ maxHeight: 420, overflowY: "auto", flex: 1 }}>
                    {offers.length === 0 ? (
                      <EmptyState label="No offers available." />
                    ) : (
                      offers.map((item, idx) => <OfferCard key={`${item.offersAndNews}-${idx}`} item={item} />)
                    )}
                  </MDBox>
                  {offers.length > 0 && (
                    <MDBox sx={{ textAlign: "center", py: 1.5 }}>
                      <ViewAllLink label="View all offers" color={COLORS.green} />
                    </MDBox>
                  )}
                </MDBox>
              </MDBox>
            </MDBox>

            {/* ---------------- MOBILE ---------------- */}
            <MDBox sx={{ display: { xs: "flex", md: "none" }, flexDirection: "column" }}>
              <MDBox sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25, alignItems: "stretch" }}>
                <PanelHeader
                  icon={<CampaignIcon sx={{ fontSize: 18 }} />}
                  iconBg="#DCEBFD"
                  iconColor={COLORS.blue}
                  title="Announcements"
                  titleColor={activeTab === "announcements" ? COLORS.blue : COLORS.text}
                  subtitle="Stay updated with the latest news and updates."
                  bg={activeTab === "announcements" ? COLORS.blueBg : "#fff"}
                  borderColor={activeTab === "announcements" ? COLORS.blue : "#E5E7EB"}
                  active={activeTab === "announcements"}
                  clickable
                  onClick={() => setActiveTab("announcements")}
                />
                <PanelHeader
                  icon={<LocalOfferOutlinedIcon sx={{ fontSize: 18 }} />}
                  iconBg={COLORS.greenIconBg}
                  iconColor={COLORS.greenDark}
                  title="Offers"
                  titleColor={COLORS.text}
                  subtitle="Take advantage of exclusive offers and discounts."
                  bg={activeTab === "offers" ? COLORS.greenBg : "#fff"}
                  borderColor={activeTab === "offers" ? COLORS.green : "#E5E7EB"}
                  active={activeTab === "offers"}
                  clickable
                  onClick={() => setActiveTab("offers")}
                />
              </MDBox>

              {activeTab === "announcements" && (
                <>
                  <MDBox sx={{ mt: 2.5, border: `1px solid ${COLORS.border}`, borderRadius: "12px", overflow: "hidden" }}>
                    {news.length === 0 ? (
                      <EmptyState label="No announcements available." />
                    ) : (
                      news.map((item, idx) => (
                        <AnnouncementRow key={`${item.offersAndNews}-${idx}`} item={item} showNew={idx === 0} />
                      ))
                    )}
                  </MDBox>
                  {news.length > 0 && (
                    <MDBox sx={{ textAlign: "center", py: 1.5 }}>
                      <ViewAllLink label="View all announcements" color={COLORS.blue} />
                    </MDBox>
                  )}

                  {offers.length > 0 && (
                    <>
                      <OfferCard item={offers[0]} />
                      <MDBox sx={{ textAlign: "center", pb: 0.5 }}>
                        <ViewAllLink
                          label="View all offers"
                          color={COLORS.green}
                          onClick={() => setActiveTab("offers")}
                        />
                      </MDBox>
                    </>
                  )}
                </>
              )}

              {activeTab === "offers" && (
                <MDBox sx={{ mt: 2.5 }}>
                  {offers.length === 0 ? (
                    <EmptyState label="No offers available." />
                  ) : (
                    offers.map((item, idx) => <OfferCard key={`${item.offersAndNews}-${idx}`} item={item} />)
                  )}
                  {news.length > 0 && (
                    <MDBox sx={{ textAlign: "center", py: 0.5 }}>
                      <ViewAllLink
                        label="View all announcements"
                        color={COLORS.blue}
                        onClick={() => setActiveTab("announcements")}
                      />
                    </MDBox>
                  )}
                </MDBox>
              )}
            </MDBox>
          </MDBox>
        </MDBox>
      </MDBox>
    </Modal>
  );
}

Popup.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default Popup;
